import { RiCloseLine, RiFileTextLine, RiUploadLine } from '@remixicon/react';
import { Accept, ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { AgButton, AgText } from 'src/components/ui';

const MAX_FILE_SIZE_BYTES = 6 * 1024 * 1024; // 6 MB

/**
 * Provides custom error messages in lieu of Dropzone's error messages, which are slightly less user-friendly.
 */
const getValidationErrorMessage = (errorCode: ErrorCode | string, acceptText: string) => {
  switch (errorCode) {
    case ErrorCode.FileInvalidType:
      return `File type must be ${acceptText}.`;
    case ErrorCode.FileTooLarge:
      return 'File must be 6 MB or smaller.';
    case ErrorCode.TooManyFiles:
      return 'Too many files. Please upload 1 file at a time.';
    // FileTooSmall error shouldn't appear because we haven't set minSize, but we will include a message just in case.
    case ErrorCode.FileTooSmall:
      return 'File is too small.';
    default:
      return 'File not accepted.';
  }
};

const defaultAccept: Accept = {
  'image/*': ['.png', '.gif', '.jpeg', '.jpg'],
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

const defaultAcceptText = 'PDF, CSV, XLS, XLSX or image';

export default function FileDropzone({
  selectedFile,
  onFileAccepted,
  onFileRejected,
  handleClearFile,
  accept = defaultAccept,
  acceptText = defaultAcceptText,
}: {
  selectedFile: File | null;
  onFileAccepted: (file: File) => void;
  onFileRejected: (validationErrorMessage: string) => void;
  handleClearFile: () => void;
  accept?: Accept;
  acceptText?: string;
}) {
  const onDropAccepted = (acceptedFiles: File[]) => {
    // Because we limit maxFiles to 1, acceptedFiles should only have a single file
    onFileAccepted(acceptedFiles[0]);
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    // For now, we are intentionally surfacing only one error at a time
    const errorCode = fileRejections[0].errors[0].code;
    onFileRejected(getValidationErrorMessage(errorCode, acceptText));
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxSize: MAX_FILE_SIZE_BYTES,
    maxFiles: 1,
    multiple: false,
    disabled: !!selectedFile,
    onDropAccepted,
    onDropRejected,
  });

  return (
    <div
      {...getRootProps({
        className:
          'min-h-[120px] flex flex-1 flex-col items-center justify-center rounded-md border-2 border-dashed border-brand-3 bg-brand-alpha-2 outline-none transition-[border] duration-200 ease-in-out',
      })}
    >
      {selectedFile ? (
        <div className="flex items-center">
          <RiFileTextLine size={24} className="mr-1 text-neutral-7" />
          <AgText size="16">{selectedFile.name}</AgText>
          <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={() => handleClearFile()}>
            <RiCloseLine size={20} />
          </AgButton>
        </div>
      ) : (
        <>
          <RiUploadLine size={40} className="text-brand-5" />
          <input {...getInputProps()} />
          <AgText size="16">Click to select a file or drag and drop</AgText>
          <AgText size="12" color="secondary">
            {acceptText} types (max. 6 MB)
          </AgText>
        </>
      )}
    </div>
  );
}
